import { toast, ToastOptions } from 'react-toastify';
import store from '@/store';
import { errorActions } from '@/store/reducers/error';

const commonUtils = {
  showToast: (message: string, options?: ToastOptions) => {
    toast(message, {
      position: 'bottom-center',
      autoClose: 4000,
      closeButton: false,
      hideProgressBar: true,
      closeOnClick: false,
      pauseOnHover: false,
      draggable: false,
      progress: undefined,
      ...(options || {}),
    });
  },

  shouldTokenRefresh: (token: string) => {
    try {
      if (token === '') {
        return true;
      }
      // Decode the token to get the payload
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(base64));

      // Check if the token has an expiration time
      if (!payload.exp) {
        console.error('Token does not contain expiration information.');
        return false;
      }

      // Get the current time in seconds since the Unix epoch
      const currentTime = Math.floor(Date.now() / 1000);

      // Check if the token expires in less than 100 seconds
      return payload.exp - currentTime < 100;
    } catch (e) {
      console.error('Error decoding token:', e);
      return false;
    }
  },

  calcPercentage: (actual: number, total: number) => {
    const percent = (100 * actual) / total;

    return isNaN(percent) ? 0 : Math.round(percent);
  },

  roundNumber: (num: number, decimals: number = 2) => {
    return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
  },

  formatString: (template: string, ...args: any[]) => {
    return template.replace(/\{(\d+)\}/g, (match, index) => {
      const value = args[parseInt(index)];
      return value !== undefined && value !== null ? value : match;
    });
  },

  isGoogle: (user: any) => {
    return user?.metadata?.type === 'google';
  },

  showGlobalError: ({
    title,
    message,
    i18nKey,
  }: {
    title?: string;
    message?: string;
    i18nKey?: string;
  }) => {
    store.dispatch(
      errorActions.showError({
        title: title || '',
        message: message || '',
        i18nKey: i18nKey || '',
      })
    );
  },

  getGradeTranslationKey: (gradeId: string | number): string => {
    // Convert to number and validate it's between 1-14
    const gradeNum = typeof gradeId === 'number' ? gradeId : parseInt(gradeId, 10);

    if (isNaN(gradeNum) || gradeNum < 1 || gradeNum > 14) {
      return `grade_1`; // Default fallback
    }

    return `grade_${gradeNum}`;
  },

  getGradeIdFromTranslationKey: (translationKey: string): string | null => {
    const normalizedKey = translationKey.toLowerCase().trim();

    if (normalizedKey.startsWith('grade_')) {
      const gradePart = normalizedKey.replace('grade_', '');

      // Handle numeric grades (1-14)
      if (/^(1[0-4]|[1-9])$/.test(gradePart)) {
        return gradePart;
      }
    }

    // Handle direct numeric input (1-14)
    if (/^(1[0-4]|[1-9])$/.test(normalizedKey)) {
      return normalizedKey;
    }

    return null;
  },

  getAllGradesForDropdown: (t: (key: string) => string): Array<{ id: string; label: string }> => {
    return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].map(id => {
      return {
        id: id.toString(),
        label: t(`grade_${id}`),
      };
    });
  },

  getGradesLabelToIdMapper: (
    t: (key: string) => string
  ): {
    [label: string]: number;
  } => {
    return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].reduce(
      (acc, id) => {
        acc[t(`grade_${id}`)] = id;
        return acc;
      },
      {} as {
        [label: string]: number;
      }
    );
  },

  /**
   * Converts a gradeId (number | null | undefined) to a translated grade name
   * @param gradeId - The grade ID (can be number, null, or undefined)
   * @param t - Translation function from react-i18next
   * @returns Translated grade name or empty string if gradeId is null/undefined
   */
  getGradeName: (gradeId: number | null | undefined, t: (key: string) => string): string => {
    if (gradeId === null || gradeId === undefined) {
      return '';
    }

    return t(`grade_${gradeId}`);
  },

  getPercentageOfActiveStudents: (students: number, activeStudents: number): number => {
    if (students <= 0) return 0;
    const percentage = (activeStudents / students) * 100;
    const rounded = Math.round(percentage);
    return Math.min(100, Math.max(0, rounded));
  },

  /**
   * Converts a grade (string | number | null | undefined) to a translated grade name
   * Only accepts integer grades (1-14)
   * @param grade - The grade value (can be string representing a number, number, null, or undefined)
   * @param t - Translation function from react-i18next
   * @returns Translated grade name or empty string if grade is invalid
   */
  getGradeText: (grade: string | number | null | undefined, t: (key: string) => string): string => {
    if (!grade) {
      return '';
    }

    let gradeId = grade.toString().toLowerCase().trim();
    gradeId = gradeId === 'university' ? '13' : gradeId === 'none' ? '14' : gradeId;

    return t(`grade_${gradeId}`);
  },

  /**
   * Normalize class API `gradeId` / legacy `grade` string for `getGradeText`
   * (e.g. class report PDF `classGrade` prop).
   */
  getClassGradeParamForReport: (
    classData: { grade?: string; gradeId?: string | number | null },
    t: (key: string) => string
  ): string => {
    if (classData.gradeId != null && String(classData.gradeId).trim() !== '') {
      return String(classData.gradeId).trim();
    }
    if (classData.grade) {
      const byLabel = commonUtils.getGradesLabelToIdMapper(t)[classData.grade];
      if (byLabel != null) return String(byLabel);
      const g = String(classData.grade).trim();
      if (/^(1[0-4]|[1-9])$/.test(g)) return g;
    }
    return '';
  },

  /**
   * Translated grade label for class screens (View Class hero, etc.).
   */
  getClassGradeDisplayLabel: (
    classData: { grade?: string; gradeId?: string | number | null },
    t: (key: string) => string
  ): string => {
    if (classData.gradeId != null && String(classData.gradeId).trim() !== '') {
      const fromId = commonUtils.getGradeText(classData.gradeId, t);
      if (fromId) return fromId;
    }
    if (classData.grade) {
      const allGrades = commonUtils.getAllGradesForDropdown(t);
      const direct = allGrades.find(gr => gr.id === classData.grade);
      if (direct) return t(commonUtils.getGradeTranslationKey(direct.id));
      const byLabel = allGrades.find(gr => {
        const key = commonUtils.getGradeTranslationKey(gr.id);
        return t(key) === classData.grade;
      });
      if (byLabel) return t(commonUtils.getGradeTranslationKey(byLabel.id));
      return classData.grade;
    }
    return '—';
  },
};

export default commonUtils;
